import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, Pencil, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const APPT_TYPES = ["shop_visit", "home_visit", "video_call"];

const fmtDate = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => {
  const x = new Date(d);
  const day = x.getDay();           // 0=Sun
  const diff = (day + 6) % 7;       // back to Monday
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
};

const blank = {
  city: "",
  area: "",
  appointment_type: "shop_visit",
  date: "",
  start_time: "10:00",
  end_time: "12:00",
  capacity: 5,
  is_active: true,
  blocked_reason: "",
};

export default function AdminAppointmentSlots() {
  const [city, setCity] = useState("");
  const [apptType, setApptType] = useState("");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const rangeStart = fmtDate(days[0]);
  const rangeEnd = fmtDate(days[6]);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch with city filter; date filter would force a single day, so we
      // fetch broad and filter client-side to the current week.
      const params = [];
      if (city.trim()) params.push(`city=${encodeURIComponent(city.trim())}`);
      const r = await api.get(`/admin/stitching/slots${params.length ? `?${params.join("&")}` : ""}`);
      const inRange = (r.data || []).filter((s) => s.date >= rangeStart && s.date <= rangeEnd);
      const filtered = apptType ? inRange.filter((s) => s.appointment_type === apptType) : inRange;
      setSlots(filtered);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load slots");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, apptType, weekStart]);

  const slotsByDate = useMemo(() => {
    const m = {};
    for (const s of slots) {
      m[s.date] ??= [];
      m[s.date].push(s);
    }
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return m;
  }, [slots]);

  const openNew = (date = null) => {
    setEditingId(null);
    setForm({ ...blank, city: city || "", date: date || fmtDate(days[0]) });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      city: s.city, area: s.area || "",
      appointment_type: s.appointment_type, date: s.date,
      start_time: s.start_time, end_time: s.end_time,
      capacity: s.capacity, is_active: s.is_active,
      blocked_reason: s.blocked_reason || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.city.trim()) return toast.error("City required");
    if (form.end_time <= form.start_time) return toast.error("End time must be after start time");
    const body = {
      city: form.city.trim(),
      area: form.area.trim() || null,
      appointment_type: form.appointment_type,
      date: form.date,
      start_time: form.start_time,
      end_time: form.end_time,
      capacity: Number(form.capacity) || 1,
      is_active: form.is_active,
      blocked_reason: form.blocked_reason.trim() || null,
    };
    try {
      if (editingId) await api.put(`/admin/stitching/slots/${editingId}`, body);
      else await api.post(`/admin/stitching/slots`, body);
      toast.success(editingId ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this slot?")) return;
    try {
      await api.delete(`/admin/stitching/slots/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const slotPillClass = (s) => {
    if (!s.is_active || s.blocked_reason) return "bg-slate-200 text-slate-700";
    const remaining = (s.capacity || 0) - (s.booked_count || 0);
    if (remaining <= 0) return "bg-red-100 text-red-900";
    if (remaining <= s.capacity / 2) return "bg-amber-100 text-amber-900";
    return "bg-emerald-100 text-emerald-900";
  };

  return (
    <div data-testid="admin-slots-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Stitching · Slots</div>
          <h1 className="font-display text-4xl">Appointment Calendar</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
            Open shop / home / video slots. Click a day to add a slot, or click any pill to edit.
          </p>
        </div>
        <button className="kb-btn-primary inline-flex items-center gap-2" onClick={() => openNew()} data-testid="slot-add-btn">
          <Plus size={14} /> New Slot
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] p-4 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="overline block mb-1">City filter</label>
          <input
            className="kb-input min-w-[180px]"
            placeholder="e.g. Bengaluru"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            data-testid="slot-city-filter"
          />
        </div>
        <div>
          <label className="overline block mb-1">Type</label>
          <select className="kb-input min-w-[160px]" value={apptType} onChange={(e) => setApptType(e.target.value)} data-testid="slot-type-filter">
            <option value="">All types</option>
            {APPT_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button className="p-2 hover:bg-[hsl(var(--kb-surface-alt))]" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="slot-prev-week">
            <ChevronLeft size={18} />
          </button>
          <div className="text-sm font-mono">
            {fmtDate(days[0])} → {fmtDate(days[6])}
          </div>
          <button className="p-2 hover:bg-[hsl(var(--kb-surface-alt))]" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="slot-next-week">
            <ChevronRight size={18} />
          </button>
          <button className="kb-btn-outline !py-1 !px-3 !text-xs ml-2" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="slot-this-week">
            This week
          </button>
        </div>
      </div>

      {/* Week grid */}
      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        <div className="grid grid-cols-7 min-w-[920px]">
          {days.map((d) => {
            const ds = fmtDate(d);
            const dayLabel = d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
            const todays = slotsByDate[ds] || [];
            const isToday = ds === fmtDate(new Date());
            return (
              <div key={ds} className={`border-r last:border-r-0 border-[hsl(var(--kb-border))] min-h-[200px] flex flex-col ${isToday ? "bg-[hsl(var(--kb-surface-alt))]/50" : ""}`} data-testid={`slot-day-${ds}`}>
                <button
                  type="button"
                  onClick={() => openNew(ds)}
                  className="text-left p-3 border-b border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))] transition-colors group"
                  data-testid={`slot-day-add-${ds}`}
                  title={`Add slot for ${dayLabel}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--kb-muted))]">{dayLabel.split(",")[0]}</div>
                  <div className="font-display text-xl flex items-center justify-between">
                    {dayLabel.split(",")[1]?.trim()}
                    <Plus size={12} className="opacity-0 group-hover:opacity-100 text-[hsl(var(--kb-muted))]" />
                  </div>
                </button>
                <div className="p-2 space-y-1.5 flex-1">
                  {todays.length === 0 ? (
                    <div className="text-[10px] text-[hsl(var(--kb-muted))] italic px-2 py-3 text-center">No slots</div>
                  ) : (
                    todays.map((s) => {
                      const remaining = Math.max(0, (s.capacity || 0) - (s.booked_count || 0));
                      return (
                        <div
                          key={s.id}
                          className={`p-2 text-xs cursor-pointer ${slotPillClass(s)} hover:opacity-80`}
                          onClick={() => openEdit(s)}
                          data-testid={`slot-pill-${s.id}`}
                        >
                          <div className="font-mono font-medium">{s.start_time}–{s.end_time}</div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className="text-[10px] uppercase tracking-wider opacity-80">{s.appointment_type.replace("_", " ")}</span>
                            <span className="text-[10px] font-mono">{s.booked_count}/{s.capacity}</span>
                          </div>
                          {s.area && <div className="text-[10px] truncate opacity-70 mt-0.5">{s.area}</div>}
                          {!s.is_active && <div className="text-[10px] mt-0.5">⊘ inactive</div>}
                          {remaining === 0 && s.is_active && <div className="text-[10px] font-medium mt-0.5">Full</div>}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {loading && <div className="text-xs text-center text-[hsl(var(--kb-muted))] py-2" data-testid="slots-loading">Loading…</div>}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-3 text-xs text-[hsl(var(--kb-muted))]">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-100 inline-block" /> Open</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-100 inline-block" /> Filling</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 inline-block" /> Full</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-200 inline-block" /> Inactive / Blocked</span>
      </div>

      {/* Slot dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="slot-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit slot" : "New slot"}</DialogTitle>
            <DialogDescription>
              Slots are city-scoped. Customers book one of these directly when capacity is available.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input className="kb-input" placeholder="City *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="slot-city" />
              <input className="kb-input" placeholder="Area (optional)" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="slot-area" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <select className="kb-input" value={form.appointment_type} onChange={(e) => setForm({ ...form, appointment_type: e.target.value })} data-testid="slot-type">
                {APPT_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
              <input type="date" className="kb-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="slot-date" />
              <input type="number" min="1" className="kb-input" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="slot-capacity" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="time" className="kb-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="slot-start" />
              <input type="time" className="kb-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="slot-end" />
            </div>
            <input className="kb-input" placeholder="Blocked reason (leaves slot inactive)" value={form.blocked_reason} onChange={(e) => setForm({ ...form, blocked_reason: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
            </label>
          </div>
          <DialogFooter>
            {editingId && (
              <button className="text-red-700 inline-flex items-center gap-1 text-xs mr-auto" onClick={() => { setDialogOpen(false); remove(editingId); }} type="button" data-testid="slot-delete-btn">
                <Trash2 size={12} /> Delete
              </button>
            )}
            <button className="kb-btn-outline" onClick={() => setDialogOpen(false)} type="button">Cancel</button>
            <button className="kb-btn-primary" onClick={save} data-testid="slot-save-btn" type="button">
              {editingId ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
